/**
 * What must never leave the server in an error report.
 *
 * This app holds Stripe keys, a Supabase service-role key, encrypted payment
 * provider tokens, and the actual takings of real businesses. An error
 * reporter is a pipe to a third party, and the default posture of most of them
 * is to attach whatever context they can find.
 *
 * The list of sensitive key names covers credentials *and* the payout
 * identity fields — a tax id and a bank account are not credentials, but they
 * are exactly as damaging in a log, and they were added to the product long
 * after this file existed. Anything new that a person would not want read
 * aloud belongs here on the day it is created.
 *
 * The rule is deny by default on *values*, never on *shape*. An earlier
 * version replaced anything past a depth limit with the string '[deep]', which
 * turned `lineno` on a stack frame from a number into text and made Sentry
 * reject the whole event — exceptions vanished while messages went through,
 * because only exceptions carry structure that deep. A scrubber that silently
 * drops the reports it is meant to protect is worse than no scrubber.
 *
 * So: blank credential-shaped keys, redact credential-shaped strings, and
 * otherwise leave every value exactly as it was, at whatever depth it lives.
 */

const SENSITIVE_KEY =
  /(secret|token|key|password|authorization|cookie|apikey|api_key|whsec|sk_live|dsn|tax_?id|rfc|clabe|ssn|ein|routing|account_?number|accountnumber|iban|ciphertext|auth_?tag)/i;

/** Stripe, Shopify and JWT key shapes. */
const SENSITIVE_VALUE = /\b(sk_live_|sk_test_|rk_live_|whsec_|shpat_|eyJ[A-Za-z0-9_-]{20,})\S*/g;

/** Deep enough for a stack frame's locals; a backstop, not a policy. */
const MAX_DEPTH = 24;

export function redactString(input: string): string {
  return input.replace(SENSITIVE_VALUE, '[redacted]');
}

export function redact(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string') return redactString(value);

  // Numbers, booleans, null, undefined and anything else primitive pass
  // through untouched — changing their type is what broke reporting before.
  if (value === null || typeof value !== 'object') return value;

  // Cycles are handled by identity rather than by depth, so legitimately deep
  // structures survive while genuinely circular ones still terminate.
  if (seen.has(value)) return '[circular]';
  if (depth > MAX_DEPTH) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((v) => redact(v, depth + 1, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_KEY.test(k) ? '[redacted]' : redact(v, depth + 1, seen);
  }
  return out;
}
